/**
 * Countries that PIXI hardware can be shipped to at checkout, and the shipping
 * region each maps to.
 *
 * NOTE: This is the interim allow-list pending GRWBKK-1547 ("[BE] Add supported
 * shipping countries and rates GraphQL query"). Once that query lands, source
 * both the country list and the country→bucket mapping from it. Country codes
 * are ISO 3166-1 alpha-2 (display names come from `Constants.Countries`).
 */

/**
 * Regional shipping buckets returned by the billing `hardwareshippingrates`
 * API in `countryBucket`. These are shipping zones, NOT ISO country codes: the
 * API keys rates by bucket, so a selected country must be translated to its
 * bucket before looking up a rate (see `shippingBucketForCountry`).
 *
 * `US`/`CA`/`GB` are country-specific buckets (and `US`/`GB` are the only ones
 * carrying `standard` service); the rest are multi-country regions.
 */
export type ShippingRegionBucket =
  | "US"
  | "CA"
  | "GB"
  | "EU"
  | "EU_NON_EU"
  | "NORTH_AMERICA"
  | "SOUTH_AMERICA"
  | "AFRICA"
  | "ASIA"
  | "MIDDLE_EAST"
  | "OCEANIA";

export const HARDWARE_CHECKOUT_ALLOWED_COUNTRIES = [
  "AU",
  "BE",
  "CA",
  "CZ",
  "DK",
  "FI",
  "FR",
  "DE",
  "GR",
  "IE",
  "IT",
  "LU",
  "MT",
  "NL",
  "NZ",
  "NO",
  "PL",
  "PT",
  "RO",
  "ES",
  "SE",
  "CH",
  "GB",
  "US",
  "TR",
  "ZA",
  "JP",
  "SG",
  "HK",
  "AE",
  "SA",
  "MX",
  "BR",
  "AR",
  "CL",
  "CO",
  "PE",
  "UY",
  "PY",
  "BO",
  "EC",
] as const;

export type AllowedShippingCountry =
  (typeof HARDWARE_CHECKOUT_ALLOWED_COUNTRIES)[number];

/**
 * Maps each allowed ISO country to its shipping-rate bucket. Typed as a
 * `Record` over the allow-list so a country can never be added without a bucket
 * (which would silently fall back to $0 shipping).
 *
 * Non-obvious calls (interim, pending GRWBKK-1547): `NO`/`CH`/`TR` → `EU_NON_EU`
 * (European, non-EU); `MX` → `NORTH_AMERICA`; EU member states → `EU`.
 */
export const SHIPPING_BUCKET_BY_COUNTRY: Record<
  AllowedShippingCountry,
  ShippingRegionBucket
> = {
  US: "US",
  CA: "CA",
  GB: "GB",
  // EU member states
  BE: "EU",
  CZ: "EU",
  DK: "EU",
  FI: "EU",
  FR: "EU",
  DE: "EU",
  GR: "EU",
  IE: "EU",
  IT: "EU",
  LU: "EU",
  MT: "EU",
  NL: "EU",
  PL: "EU",
  PT: "EU",
  RO: "EU",
  ES: "EU",
  SE: "EU",
  // European, non-EU
  NO: "EU_NON_EU",
  CH: "EU_NON_EU",
  TR: "EU_NON_EU",
  // Oceania
  AU: "OCEANIA",
  NZ: "OCEANIA",
  // Africa
  ZA: "AFRICA",
  // Asia
  JP: "ASIA",
  SG: "ASIA",
  HK: "ASIA",
  // Middle East
  AE: "MIDDLE_EAST",
  SA: "MIDDLE_EAST",
  // North America (excluding the specific US/CA buckets)
  MX: "NORTH_AMERICA",
  // South America
  BR: "SOUTH_AMERICA",
  AR: "SOUTH_AMERICA",
  CL: "SOUTH_AMERICA",
  CO: "SOUTH_AMERICA",
  PE: "SOUTH_AMERICA",
  UY: "SOUTH_AMERICA",
  PY: "SOUTH_AMERICA",
  BO: "SOUTH_AMERICA",
  EC: "SOUTH_AMERICA",
};

export const DEFAULT_HARDWARE_CHECKOUT_COUNTRY = "US";

/**
 * Translates a selected ISO country code to its shipping-rate bucket, so the
 * checkout can look up a rate from the bucket-keyed `hardwareshippingrates`
 * response. Returns `undefined` for an unknown country (no rate → $0 shipping).
 */
export const shippingBucketForCountry = (
  code: string | undefined,
): ShippingRegionBucket | undefined =>
  code ? SHIPPING_BUCKET_BY_COUNTRY[code as AllowedShippingCountry] : undefined;
